import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { api } from '../../lib/api';
import {
  Building2, Users, Phone, Mail, Globe, MapPin, Clock,
  ShieldCheck, Send, Star, Calendar,
} from 'lucide-react';
import { ContactRow } from './sharedAtoms';
import RevealMobile from './RevealMobile';
import { formatHoursDisplay, isOpenNow } from '../../lib/businessHours';

/** "Contact Us" tab: vendor details + embedded map + message form + 'why choose us' card. */
const SupplierContactTab = ({ supplier, certificates, onSwitchToCerts }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', mobile: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) {
      toast({ title: 'Please fill name and mobile', variant: 'destructive' });
      return;
    }
    try {
      setSubmitting(true);
      await api.createEnquiry({ ...form, product_id: null, product_name: `Contact via ${supplier?.name}` });
      toast({ title: 'Message sent', description: 'The vendor will reach out shortly.' });
      setForm({ name: '', mobile: '', email: '', message: '' });
    } catch (err) {
      toast({ title: 'Failed to send', description: err?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  // Prefer exact lat/lng (from the vendor's map-picked pin) when available;
  // otherwise fall back to a text geocode of the address/city.
  const hasGeo = typeof supplier.latitude === 'number' && typeof supplier.longitude === 'number';
  const mapsQuery = encodeURIComponent(`${supplier.address ? supplier.address + ', ' : ''}${supplier.city || 'Hyderabad'}, Telangana, India`);
  const mapSrc = hasGeo
    ? `https://www.google.com/maps?q=${supplier.latitude},${supplier.longitude}&z=16&output=embed`
    : `https://www.google.com/maps?q=${mapsQuery}&output=embed`;

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="space-y-4">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Contact Details</h2>
          <div className="space-y-4 text-sm">
            <ContactRow icon={Building2} label="Company" value={supplier.name} />
            {supplier.contact_name && <ContactRow icon={Users} label="Contact Person" value={supplier.contact_name} />}
            <ContactRow icon={Phone} label="Phone" value={<RevealMobile supplier={supplier} />} />
            <ContactRow icon={Mail} label="Email" value={supplier.email || `contact@${supplier.id}.com`} />
            {supplier.website && (
              <ContactRow
                icon={Globe}
                label="Website"
                value={(
                  <a
                    href={supplier.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#2c54b2] hover:underline break-all"
                    data-testid="supplier-website-link"
                  >
                    {supplier.website.replace(/^https?:\/\//, '')}
                  </a>
                )}
              />
            )}
            <ContactRow icon={MapPin} label="Address" value={supplier.address ? `${supplier.address}, ${supplier.city || ''}` : supplier.city} />
            <ContactRow
              icon={Clock}
              label="Business Hours"
              value={(() => {
                const formatted = formatHoursDisplay(supplier.business_hours);
                const open = isOpenNow(supplier.business_hours);
                if (!formatted) {
                  return <span className="text-gray-500 italic">Contact for hours</span>;
                }
                return (
                  <div className="flex flex-col gap-1">
                    <span>{formatted}</span>
                    {open !== null && (
                      <span
                        className={`inline-flex items-center gap-1 self-start text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
                          open
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : 'bg-gray-50 text-gray-500 border border-gray-200'
                        }`}
                        data-testid="supplier-open-status"
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${open ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                        {open ? 'Open now' : 'Closed now'}
                      </span>
                    )}
                  </div>
                );
              })()}
            />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-3 overflow-hidden" data-testid="supplier-map-wrap">
          <h3 className="text-sm font-semibold text-gray-900 mb-2 px-3 pt-2 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#ee5e23]" /> Vendor Location
          </h3>
          <iframe
            title="Vendor location on Google Maps"
            src={mapSrc}
            className="w-full h-64 rounded border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
            data-testid="supplier-map-iframe"
          />
          <a
            href={hasGeo
              ? `https://www.google.com/maps/search/?api=1&query=${supplier.latitude},${supplier.longitude}`
              : `https://www.google.com/maps/search/?api=1&query=${mapsQuery}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block mt-2 px-3 pb-2 text-xs text-[#2c54b2] hover:underline"
            data-testid="supplier-map-open"
          >
            Open in Google Maps →
          </a>
        </div>

        {certificates.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800 flex items-start gap-2" data-testid="contact-certs-hint">
            <ShieldCheck className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              {supplier.name} has <b>{certificates.length} verified certificate{certificates.length === 1 ? '' : 's'}</b>.{' '}
              <button type="button" onClick={onSwitchToCerts} className="underline font-medium hover:text-green-900" data-testid="contact-certs-link">View certificates →</button>
            </div>
          </div>
        )}

        <div className="bg-gradient-to-br from-[#2c54b2] to-[#1e3a8a] text-white rounded-lg p-6">
          <h3 className="text-lg font-bold mb-2">Why choose {supplier.name}?</h3>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-green-300" /> {supplier.gst}</li>
            <li className="flex items-center gap-2"><Star className="w-4 h-4 text-amber-300 fill-amber-300" /> {supplier.rating} / 5 buyer rating</li>
            <li className="flex items-center gap-2"><Clock className="w-4 h-4 text-blue-200" /> {supplier.response} response rate</li>
            <li className="flex items-center gap-2"><Calendar className="w-4 h-4 text-blue-200" /> {new Date().getFullYear() - (supplier.established || new Date().getFullYear())}+ years of experience</li>
          </ul>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-1">Send a Message</h2>
        <p className="text-sm text-gray-600 mb-4">Get in touch with {supplier.name} directly.</p>
        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <Label className="text-xs">Your Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Mobile *</Label>
              <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="+91" />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Message *</Label>
            <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder="What are you looking for? Mention products, quantities, target price..." rows={5} />
          </div>
          <div className="text-xs text-gray-500 flex items-center gap-2 bg-blue-50 p-2 rounded">
            <ShieldCheck className="w-4 h-4 text-green-600" />
            Your details are safe and only shared with this vendor.
          </div>
          <Button type="submit" disabled={submitting} className="w-full bg-[#ee5e23] hover:bg-[#d24e15] text-white h-11">
            <Send className="w-4 h-4 mr-2" />
            {submitting ? 'Sending...' : 'Send Message'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default SupplierContactTab;
